/**
 * Hooks de dados da tela Configuração de Bonds (Brief 4 · Tela 9 · ADR-037/131).
 *
 * Consome o overlay real (apps/engine/src/modules/bond-type-configs):
 *   - GET   /v1/bond-type-configs          (catálogo mesclado) · gate `bond-type-config-read`
 *   - PATCH /v1/bond-type-configs/:bondType (toggle enabled)    · gate `bond-type-config-manage`
 *
 * A query é GATEADA por capability no `enabled` (não só no render) — sem
 * `bond-type-config-read` o fetch nem dispara, evitando 403 vazando em toast
 * (lição "gatear o fetch, não só o render"). O toggle usa If-Match (ADR-071) com
 * a `version` carregada do GET (v0 quando o tipo ainda não tem linha no tenant);
 * 409 (optimistic lock) propaga ao chamador, que trata de forma não-destrutiva.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useCapability } from '../../hooks/use-capability';
import { api } from '../../lib/api-client';

import type { BondTypeConfigView, UpdateBondTypeConfigBody } from './types';
import type { UseMutationResult } from '@tanstack/react-query';

const LIST_QUERY_KEY = 'bond-type-configs-list';
const STALE_TIME = 60_000;

export function useBondTypeConfigList(): {
  data: BondTypeConfigView[];
  isLoading: boolean;
  isError: boolean;
  canRead: boolean;
} {
  const canRead = useCapability('bond-type-config-read');
  const query = useQuery({
    queryKey: [LIST_QUERY_KEY],
    queryFn: () => api.listBondTypeConfigs(),
    enabled: canRead,
    staleTime: STALE_TIME,
    retry: 1,
  });

  return {
    data: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    canRead,
  };
}

/**
 * Liga/desliga um tipo de Bond no tenant (PATCH · gate `bond-type-config-manage`).
 * Em sucesso invalida a lista (refetch traz a `version` incrementada e a contagem
 * atualizada). 409 (versão divergente) propaga ao chamador — a view refaz o fetch
 * e mostra aviso não-destrutivo, sem perder estado.
 */
export function useToggleBondTypeConfig(): UseMutationResult<
  BondTypeConfigView,
  unknown,
  { bondType: string; expectedVersion: number; enabled: boolean }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ bondType, expectedVersion, enabled }) => {
      const body: UpdateBondTypeConfigBody = { enabled };
      return api.updateBondTypeConfig(bondType, expectedVersion, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [LIST_QUERY_KEY] });
    },
  });
}
